import React from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';

interface UnavailableStubProps {
  title?: string;
  message?: string;
  buttonText?: string;
  onRetry?: () => void;
  /** Для экрана ошибки рендера (без кнопки "Повторить", только "Обновить страницу") */
  isErrorBoundary?: boolean;
}

/**
 * Заглушка при недоступности сервиса или ошибке загрузки.
 * Единый стиль: градиент как в приложении, карточка, без серого фона.
 */
export const UnavailableStub: React.FC<UnavailableStubProps> = ({
  title,
  message,
  buttonText,
  onRetry,
  isErrorBoundary = false,
}) => {
  const handleRetry = () => {
    if (isErrorBoundary) {
      window.location.reload();
    } else {
      onRetry?.();
    }
  };

  return (
    <div className="min-h-dvh min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-stone-200 via-stone-100 to-stone-200 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <div className="absolute inset-0 backdrop-blur-sm bg-white/10 dark:bg-black/10" />
      <div className="relative z-10 w-full max-w-md">
        <div className="rounded-2xl border border-stone-200/80 dark:border-slate-600/50 bg-white/90 dark:bg-slate-800/90 shadow-xl shadow-stone-200/50 dark:shadow-black/20 p-6 sm:p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400">
              <WifiOff className="h-7 w-7" />
            </div>
          </div>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white mb-2">
            {title ?? (isErrorBoundary ? 'Ошибка загрузки' : 'Сервис временно недоступен')}
          </h2>
          <p className="text-sm text-stone-600 dark:text-slate-400 mb-6">
            {message ?? (isErrorBoundary
              ? 'Что-то пошло не так. Попробуйте обновить страницу.'
              : 'Проверьте подключение к интернету или попробуйте позже.')}
          </p>
          <button
            type="button"
            onClick={handleRetry}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            {buttonText ?? (isErrorBoundary ? 'Обновить страницу' : 'Повторить')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnavailableStub;
